import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import BatchPricingModal from '../components/BatchPricingModal';
import FinancialDetailsModal from '../components/FinancialDetailsModal';

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  type: 'feature' | 'bug';
  priority: string;
  status: string;
  board_status: string;
  requester_name: string | null;
  observations: string | null;
  estimate_hours: number;
  estimate_days: number;
  value: number;
  paid: boolean;
  sprint_desired: boolean;
  sprint_id: string | null;
  position: number;
  created_by: string | null;
  created_at: string;
  // Campos financeiros adicionais (mockados por enquanto)
  approved_value?: number;
  payment_date?: string;
  payment_method?: string;
  financial_notes?: string;
  billing_category?: string;
}

interface Sprint {
  id: string;
  name: string;
  status: string;
}

interface Project {
  id: string;
  name: string;
  client_id?: string;
  _client_name?: string;
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  low: { label: 'Baixa', color: 'text-slate-400', icon: 'ri-arrow-down-line' },
  medium: { label: 'Média', color: 'text-orange-400', icon: 'ri-arrow-right-line' },
  high: { label: 'Alta', color: 'text-red-500', icon: 'ri-arrow-up-line' },
  critical: { label: 'Crítica', color: 'text-red-700', icon: 'ri-arrow-up-double-line' },
};

const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'bank_transfer', label: 'Transferência' },
  { value: 'cash', label: 'Dinheiro' },
];

export default function PricingPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuthContext();
  const isAdmin = profile?.role === 'admin';

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [financialModalOpen, setFinancialModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterPaid, setFilterPaid] = useState('');
  const [filterSprint, setFilterSprint] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'priority' | 'created_at' | 'value'>('priority');
  const [showUnpricedOnly, setShowUnpricedOnly] = useState(false);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);

    const [projRes, tasksRes, sprintsRes] = await Promise.all([
      supabase.from('projects').select('id, name, client_id').eq('id', projectId).maybeSingle(),
      supabase.from('tasks').select('*').eq('project_id', projectId).order('created_at', { ascending: false }),
      supabase.from('sprints').select('*').eq('project_id', projectId).order('created_at'),
    ]);

    if (projRes.data) {
      const { data: client } = projRes.data.client_id
        ? await supabase.from('clients').select('name').eq('id', projRes.data.client_id).maybeSingle()
        : { data: null };
      setProject({ ...projRes.data, _client_name: client?.name || '—' });
    }

    setTasks(tasksRes.data || []);
    setSprints(sprintsRes.data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = tasks.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.title.toLowerCase().includes(q) || (t.requester_name || '').toLowerCase().includes(q);
    const matchType = !filterType || t.type === filterType;
    const matchPriority = !filterPriority || t.priority === filterPriority;
    const matchPaid = !filterPaid || (filterPaid === 'paid' ? t.paid : !t.paid);
    const matchSprint = !filterSprint
      ? true
      : filterSprint === 'desired'
      ? t.sprint_desired
      : t.sprint_id === filterSprint;
    const matchUnpriced = !showUnpricedOnly || (t.value === 0 && !t.paid);
    return matchSearch && matchType && matchPriority && matchPaid && matchSprint && matchUnpriced;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'priority') {
      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      return (order[a.priority as keyof typeof order] ?? 99) - (order[b.priority as keyof typeof order] ?? 99);
    }
    if (sortBy === 'created_at') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    return (b.value || 0) - (a.value || 0);
  });

  const toggleSelection = (taskId: string) => {
    const newSet = new Set(selectedTasks);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setSelectedTasks(newSet);
  };

  const toggleAll = () => {
    if (selectedTasks.size === sorted.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(sorted.map(t => t.id)));
    }
  };

  const handleBatchUpdate = async (data: {
    value: number;
    estimate_days: number;
    billing_category: string;
    mode: 'individual' | 'total';
  }) => {
    const taskIds = Array.from(selectedTasks);
    if (taskIds.length === 0) return;

    let valuePerTask = data.value;
    if (data.mode === 'total' && taskIds.length > 0) {
      valuePerTask = data.value / taskIds.length;
    }

    for (const taskId of taskIds) {
      await supabase.from('tasks').update({
        value: valuePerTask,
        estimate_days: data.estimate_days,
        billing_category: data.billing_category,
        updated_at: new Date().toISOString(),
      }).eq('id', taskId);
    }

    await logAudit('batch_update', 'tasks', taskIds.join(','), { value: valuePerTask, count: taskIds.length });
    setSelectedTasks(new Set());
    setBatchModalOpen(false);
    loadData();
  };

  const handleMarkAsPaid = async (task: Task, paymentData: {
    value: number;
    payment_date: string;
    payment_method: string;
    financial_notes: string;
  }) => {
    // Atualizar a task
    const { error } = await supabase.from('tasks').update({
      paid: true,
      value: paymentData.value,
      updated_at: new Date().toISOString(),
    }).eq('id', task.id);

    if (!error) {
      // Criar lançamento financeiro
      await supabase.from('financial_entries').insert({
        project_id: projectId,
        client_id: project?.client_id,
        task_id: task.id,
        type: 'income',
        category: task.billing_category || 'Desenvolvimento',
        description: `Pagamento: ${task.title}`,
        amount: paymentData.value,
        entry_date: paymentData.payment_date,
        payment_method: paymentData.payment_method,
        notes: paymentData.financial_notes,
        is_paid: true,
        paid_at: new Date().toISOString(),
        created_by: profile?.id,
      });

      // Criar crédito no caixa
      await supabase.from('cash_flow').insert({
        project_id: projectId,
        client_id: project?.client_id,
        task_id: task.id,
        flow_type: 'credit',
        origin: 'task',
        amount: paymentData.value,
        description: `Recebimento: ${task.title}`,
        payment_method: paymentData.payment_method,
        reference_date: paymentData.payment_date,
        created_by: profile?.id,
      });

      await logAudit('mark_paid', 'tasks', task.id, { value: paymentData.value });
      setFinancialModalOpen(false);
      setEditingTask(null);
      loadData();
    }
  };

  const openFinancialModal = (task: Task) => {
    setEditingTask(task);
    setFinancialModalOpen(true);
  };

  const sprintName = (id: string | null) => {
    if (!id) return null;
    return sprints.find((s) => s.id === id)?.name || null;
  };

  const totalValue = sorted.reduce((sum, t) => sum + (t.value || 0), 0);
  const totalPaid = sorted.reduce((sum, t) => sum + (t.paid ? t.value || 0 : 0), 0);
  const totalPending = totalValue - totalPaid;

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/scrum')}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base"></i>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-slate-800 text-xl font-bold">{project?.name || '...'}</h1>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Precificação</span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5">{project?._client_name} · {filtered.length} itens</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/admin/scrum/${projectId}/backlog`)}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-list-check text-sm"></i>
            Backlog
          </button>
          <button
            onClick={() => navigate(`/admin/scrum/${projectId}/board`)}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-kanban-view text-sm"></i>
            Board
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Valor Total</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">
            {totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Pago</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">
            {totalPaid.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Pendente</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">
            {totalPending.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
        </div>
      </div>

      {/* Barra de Ações em Lote */}
      {selectedTasks.size > 0 && (
        <div className="bg-slate-800 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white text-sm font-medium">
              {selectedTasks.size} item{selectedTasks.size !== 1 ? 's' : ''} selecionado{selectedTasks.size !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBatchModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-price-tag-3-line"></i>
              Definir Preço
            </button>
            <button
              onClick={() => setSelectedTasks(new Set())}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por título ou solicitante..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Todos os tipos</option>
          <option value="feature">Nova Demanda</option>
          <option value="bug">Bug/Correção</option>
        </select>
        <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Todas as prioridades</option>
          <option value="critical">Crítica</option>
          <option value="high">Alta</option>
          <option value="medium">Média</option>
          <option value="low">Baixa</option>
        </select>
        <select value={filterPaid} onChange={(e) => setFilterPaid(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Todos</option>
          <option value="paid">Pago</option>
          <option value="unpaid">Pendente</option>
        </select>
        <select value={filterSprint} onChange={(e) => setFilterSprint(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Todas as sprints</option>
          <option value="desired">Desejados para próxima sprint</option>
          {sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'priority' | 'created_at' | 'value')} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="priority">Ordenar por prioridade</option>
          <option value="created_at">Ordenar por data</option>
          <option value="value">Ordenar por valor</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={showUnpricedOnly}
            onChange={(e) => setShowUnpricedOnly(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-300 cursor-pointer"
          />
          <span className="text-sm text-slate-600">Sem preço</span>
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-emerald-500 text-2xl"></i>
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-money-dollar-circle-line text-5xl mb-4"></i>
          <p className="text-sm font-medium text-slate-500">Nenhum item encontrado</p>
          <p className="text-xs mt-1">Ajuste os filtros ou adicione itens ao backlog</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium w-10">
                    <input
                      type="checkbox"
                      checked={selectedTasks.size === sorted.length && sorted.length > 0}
                      onChange={toggleAll}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-300 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Item</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Prioridade</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Estimativa</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Valor</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Status</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((task) => {
                  const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <tr key={task.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${selectedTasks.has(task.id) ? 'bg-emerald-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedTasks.has(task.id)}
                          onChange={() => toggleSelection(task.id)}
                          className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-300 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-slate-800 font-medium truncate">{task.title}</p>
                        <p className="text-slate-400 text-xs truncate mt-0.5">{task.requester_name || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'}`}>
                          <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                          {task.type === 'bug' ? 'Bug' : 'Demanda'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${prio.color}`}>
                          <i className={prio.icon}></i>
                          {prio.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {task.estimate_hours > 0 ? `${task.estimate_hours}h` : ''}
                        {task.estimate_hours > 0 && task.estimate_days > 0 ? ' / ' : ''}
                        {task.estimate_days > 0 ? `${task.estimate_days}d` : ''}
                        {!task.estimate_hours && !task.estimate_days ? '—' : ''}
                      </td>
                      <td className="px-4 py-3">
                        {task.value > 0 ? (
                          <span className="text-slate-700 text-sm font-medium">
                            {task.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">Sem preço</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {task.paid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                            <i className="ri-checkbox-circle-fill"></i>
                            Pago
                          </span>
                        ) : task.value > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
                            <i className="ri-time-line"></i>
                            Pendente
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                            <i className="ri-price-tag-3-line"></i>
                            Orçar
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {!task.paid && (
                            <button
                              onClick={() => openFinancialModal(task)}
                              title={task.value > 0 ? 'Marcar como pago' : 'Definir valor e pagamento'}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-money-dollar-circle-line text-sm"></i>
                            </button>
                          )}
                          {task.paid && (
                            <button
                              title="Pago"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-500 cursor-default"
                            >
                              <i className="ri-checkbox-circle-fill text-sm"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {batchModalOpen && (
        <BatchPricingModal
          selectedCount={selectedTasks.size}
          onClose={() => setBatchModalOpen(false)}
          onSave={handleBatchUpdate}
        />
      )}

      {financialModalOpen && editingTask && (
        <FinancialDetailsModal
          task={editingTask}
          onClose={() => { setFinancialModalOpen(false); setEditingTask(null); }}
          onSave={handleMarkAsPaid}
        />
      )}
    </AppLayout>
  );
}